import { computed, effect, inject, Injectable, InjectionToken, signal } from '@angular/core';
import {
  AuthProvider,
  browserLocalPersistence,
  FacebookAuthProvider,
  getRedirectResult,
  GoogleAuthProvider,
  onAuthStateChanged,
  setPersistence,
  signInWithPopup,
  signInWithRedirect,
  signOut,
  TwitterAuthProvider,
  User,
} from 'firebase/auth';
import { FIREBASE_AUTH } from './firebase';
import { I18nService } from './i18n-service';
import { signsInByLeaving } from './installed';

/** Firebase answers about a stored session in milliseconds, or it never will. */
const SESSION_TIMEOUT = 8000;

/**
 * Firebase takes about nine seconds to admit the window was closed, and on a
 * phone it sometimes never does. This is the point at which the button stops
 * waiting and lets the person try again.
 */
const SIGN_IN_TIMEOUT = 30_000;

/** What Firebase says when the window never opened, as against being closed. */
const WINDOW_REFUSED = new Set([
  'auth/popup-blocked',
  'auth/operation-not-supported-in-this-environment',
]);

type WatchSession = (seen: (user: User | null) => void, failed: () => void) => void;
/** `null` means the page is leaving to sign in and will come back. */
type OpenSignIn = (provider: AuthProvider) => Promise<User | null>;
type SignInReturn = () => Promise<User | null>;

/** The calls are tokens, so a test can answer for the sign-in window. */
export const WATCH_SESSION = new InjectionToken<WatchSession>('WATCH_SESSION', {
  providedIn: 'root',
  factory: () => {
    const auth = inject(FIREBASE_AUTH);
    return (seen, failed) => void onAuthStateChanged(auth, seen, failed);
  },
});

export const OPEN_SIGN_IN = new InjectionToken<OpenSignIn>('OPEN_SIGN_IN', {
  providedIn: 'root',
  factory: () => {
    const auth = inject(FIREBASE_AUTH);
    return async (provider) => {
      await setPersistence(auth, browserLocalPersistence);
      if (signsInByLeaving()) {
        await signInWithRedirect(auth, provider);
        return null; // The page is on its way out; nothing follows this.
      }
      return (await signInWithPopup(auth, provider)).user;
    };
  },
});

/** Whoever left to sign in is asked for on the way back in. */
export const SIGN_IN_RETURN = new InjectionToken<SignInReturn>('SIGN_IN_RETURN', {
  providedIn: 'root',
  factory: () => {
    const auth = inject(FIREBASE_AUTH);
    return async () => (await getRedirectResult(auth))?.user ?? null;
  },
});

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly auth = inject(FIREBASE_AUTH);
  private readonly i18n = inject(I18nService);
  private readonly watchSession = inject(WATCH_SESSION);
  private readonly openSignIn = inject(OPEN_SIGN_IN);
  private readonly signInReturn = inject(SIGN_IN_RETURN);

  /** `undefined` while Firebase resolves the session, `null` when signed out. */
  private readonly state = signal<User | null | undefined>(undefined);

  readonly user = this.state.asReadonly();
  readonly loading = computed(() => this.state() === undefined);
  readonly signedIn = computed(() => !!this.state());

  /** Only the button waits for the sign-in window; the rest of the app does not. */
  readonly signingIn = signal(false);

  /** The window never opened. Unlike a closed one, that is worth saying. */
  readonly blocked = signal(false);

  constructor() {
    this.watchSession(
      (user) => {
        this.state.set(user);
        // Whoever signed in is in, even if the window never said so.
        if (user) {
          this.signingIn.set(false);
        }
      },
      () => this.state.set(null),
    );

    // This load may be the way back from signing in on another page.
    void this.signInReturn()
      .then((user) => user && this.state.set(user))
      .catch(() => undefined);

    // Nothing arrived, and no answer is an answer: signed out, and the app is
    // usable. Firebase is still free to correct it whenever it does reply.
    setTimeout(() => {
      if (this.state() === undefined) {
        this.state.set(null);
      }
    }, SESSION_TIMEOUT);

    // Google, Facebook and X show their sign-in window in this language.
    effect(() => (this.auth.languageCode = this.i18n.lang()));
  }

  signInWithGoogle(): Promise<void> {
    return this.signIn(new GoogleAuthProvider());
  }

  signInWithFacebook(): Promise<void> {
    return this.signIn(new FacebookAuthProvider());
  }

  signInWithTwitter(): Promise<void> {
    return this.signIn(new TwitterAuthProvider());
  }

  async signOut(): Promise<void> {
    try {
      await signOut(this.auth);
    } finally {
      this.state.set(null);
    }
  }

  /**
   * This opens the sign-in window, and that window does not always come back —
   * a phone that swallows the popup, a browser that blocks it quietly. So the
   * wait is on a leash: the button stops spinning either way, and whoever
   * really signed in arrives through the session watch above.
   *
   * An installed app that can bring the round trip home leaves the page
   * instead, and then this never returns.
   */
  private async signIn(provider: AuthProvider): Promise<void> {
    if (this.signingIn()) {
      return;
    }

    this.signingIn.set(true);
    this.blocked.set(false);
    const giveUp = setTimeout(() => this.signingIn.set(false), SIGN_IN_TIMEOUT);

    try {
      const user = await this.openSignIn(provider);
      if (user) {
        this.state.set(user);
      }
    } catch (refused) {
      // Closed or refused, the page is where it was. Only a window that never
      // opened is said out loud: on an installed iPhone none ever will.
      const code = (refused as { code?: string })?.code;
      this.blocked.set(!!code && WINDOW_REFUSED.has(code));
    } finally {
      clearTimeout(giveUp);
      this.signingIn.set(false);
    }
  }
}
